<script setup lang="ts">
import { useScenarioFormComposable } from '@/components/scenario-form/composable/useScenarioFormComposable'
import type { ScenarioFormMixinProps } from '@/components/scenario-form/type/type'
import { ref, watch, onMounted, onUnmounted, computed } from 'vue'
import * as L from 'leaflet'
import {
  downloadFile,
  displayGeometryOnMap,
  handleShapeCreated,
  handleDrawStart,
  handleDownload,
  handleReRun as handleReRunUtil
} from '@/components/scenario-form/utils/DataDownloaderUtils'
import { docUrl } from '@/components/scenario-form/constants/constants'
import 'leaflet-draw'
import 'leaflet/dist/leaflet.css'
import 'leaflet-draw/dist/leaflet.draw.css'
import 'leaflet-draw/dist/leaflet.draw-src.js'
import { useGenerateInputStore } from '@/stores/generateInput'

const props = defineProps<ScenarioFormMixinProps>()
const emit = defineEmits(['updateTabCompletion', 'shapeComplete', 'change-tab'])
const skip = ref(false)
const isLoading = ref(false)
const loadingMessage = ref('Processing, please wait...')
const pullingInterval = ref<any>(null)

const warningMessage = ref('')
const showWarning = ref(false)

const showWarningBox = (message: string) => {
  warningMessage.value = message
  showWarning.value = true
}

const customIsComplete = (): boolean => {
  return skip.value
}

const doc_url = docUrl

const extraPath = 'data-preparation'

function navigateToPrepareData() {
  const url = doc_url ? doc_url + extraPath : '/docs/data-preparation'
  window.open(url, '_blank')
}

window.navigateToPrepareData = navigateToPrepareData

const getFileDisabled = ref(true)

const reRunDisabled = ref(false)

watch(
  () => useGenerateInputStore().currentItem?.status,
  (status) => {
    getFileDisabled.value =
      status === 'Draft' || status === 'Pending' || status === 'Running' || status === 'Failed'

    reRunDisabled.value = status === 'Draft' || status === 'Pending' || status === 'Running'
  },
  { immediate: true }
)

const { currentItem, isValid, formRef, disabled, isReadOnly, isComplete } =
  useScenarioFormComposable(props, emit, customIsComplete)
defineExpose({ formRef })

watch(isComplete, (value) => {
  emit('updateTabCompletion', props.tab, value)
})

const markComplete = () => {
  if (formRef.value?.validate()) {
    skip.value = true
    emit('change-tab', 'PROJECT_DETAILS')
  } else if (isReadOnly.value) {
    skip.value = true
    emit('change-tab', 'PROJECT_DETAILS')
  }
}

const disableMapFunction = () => {
  if (currentItem.value && currentItem.value.status === 'Draft') {
    return false
  } else {
    return true
  }
}

let map: any = null
let drawnItems: any = null

const initializeMap = () => {
  map = L.map('leaflet-map', {
    center: [20, 0],
    zoom: 2,
    zoomControl: false,
    attributionControl: false
  })

  L.tileLayer(
    'https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}',
    { maxZoom: 19 }
  ).addTo(map)

  L.control.zoom({ position: 'topright' }).addTo(map)

  const downloadBtn = L.Control.extend({
    options: { position: 'topright' },
    onAdd: function () {
      const btn = L.DomUtil.create('button', 'leaflet-bar')
      btn.title = 'Download'

      btn.innerHTML = '<span style="font-size: 16px;"><span class="mdi mdi-download"></span>'

      btn.style.width = '34px'
      btn.style.height = '34px'
      btn.style.backgroundColor = 'white'
      btn.style.border = '1px solid #ccc'
      btn.style.borderRadius = '4px'
      btn.style.cursor = 'pointer'
      btn.onclick = (event: Event) =>
        handleDownload(
          event,
          useGenerateInputStore(),
          currentItem,
          drawnItems,
          isLoading,
          loadingMessage,
          pullingInterval,
          getFileDisabled,
          reRunDisabled,
          showWarningBox
        )
      return btn
    }
  })
  map.addControl(new downloadBtn())

  drawnItems = new L.FeatureGroup()
  map.addLayer(drawnItems)

  const drawControl = new L.Control.Draw({
    position: 'topright',
    draw: disableMapFunction()
      ? undefined
      : {
          polygon: {},
          rectangle: false,
          circle: false,
          marker: false,
          polyline: false,
          circlemarker: false
        },
    edit: disableMapFunction()
      ? undefined
      : {
          featureGroup: drawnItems,
          remove: true
        }
  })

  if (!disableMapFunction()) {
    map.addControl(drawControl)
    map.on('draw:deleted', handleShapeDeleted)
    map.on(L.Draw.Event.CREATED, (e: any) => handleShapeCreated(e, drawnItems, showWarningBox))
    map.on('draw:drawstart', (e: any) => handleDrawStart(e, drawnItems, map, showWarningBox))
  }
}

const startPulling = (generateInputStore: any) => {
  pullingInterval.value = setInterval(async () => {
    try {
      if (!currentItem.value) {
        return
      }
      const scenarioId = currentItem.value.id
      await generateInputStore.fetchGenerateInputs(scenarioId)

      const currentGenerateInput = generateInputStore.currentItem
      if (currentGenerateInput?.status === 'Success') {
        loadingMessage.value = 'Generate Data Successfully!'
        isLoading.value = true
        clearInterval(pullingInterval.value)
        pullingInterval.value = null
      } else if (currentGenerateInput?.status === 'Failed') {
        reRunDisabled.value = false
        loadingMessage.value = 'The process failed. Please try again.'
        isLoading.value = true
        clearInterval(pullingInterval.value)
        pullingInterval.value = null
      }
    } catch (error) {
      console.error('Error during pulling:', error)
    }
  }, 1000)
}

const stopPulling = () => {
  if (pullingInterval.value) {
    clearInterval(pullingInterval.value)
    pullingInterval.value = null
  }
  isLoading.value = false
}

const handleShapeDeleted = () => {}

const isReRunVisible = computed(() => {
  const generateInputStore = useGenerateInputStore()
  const currentStatus = generateInputStore.currentItem?.status
  return currentStatus === 'Failed'
})

const handleReRun = async () => {
  handleReRunUtil(
    currentItem,
    drawnItems,
    isLoading,
    loadingMessage,
    reRunDisabled,
    showWarningBox,
    startPulling,
    useGenerateInputStore
  )
}

onMounted(() => {
  const generateInputStore = useGenerateInputStore()
  const scenarioId = currentItem.value ? currentItem.value.id : null

  initializeMap()
  if (scenarioId !== null) {
    generateInputStore.fetchGenerateInputs(scenarioId).then(() => {
      if (generateInputStore.currentItem?.geometry) {
        displayGeometryOnMap(generateInputStore.currentItem.geometry, drawnItems, map)
      }
    })
  }
})

onUnmounted(() => {
  if (map) {
    map.off()
    map.remove()
  }
  stopPulling()
  const generateInputStore = useGenerateInputStore()
  generateInputStore.resetAll()
})
</script>

<template>
  <v-form ref="formRef" v-model="isValid" :disabled="disabled" :readonly="isReadOnly">
    <div class="data-prep-header">
      <div class="data-prep-title">Data preparation for the road network</div>
      <div class="data-prep-desc">
        The road network is a required input data layer and needs specific preparation steps to be
        usable (<span
          class="text-primary"
          @click="navigateToPrepareData"
          style="
            color: #009eab;
            font-size: 1rem;
            text-decoration: none;
            font-family: 'Roboto', Arial, sans-serif;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
          "
          >manual</span
        >). To simplify, use the map below to download a pre-prepared road network.
        <b
          >We recommend this step to avoid errors, even for experienced users. Maximum shape area
          size is 1000000 km2.</b
        >
      </div>
      <ul class="data-prep-list">
        <li>
          <v-icon color="success" size="12" class="mr-1">mdi-check</v-icon>
          By clicking on this icon
          <v-icon color="primary" size="12" class="mx-1" style="vertical-align: middle"
            >mdi-shape-polygon-plus</v-icon
          >
          you can create a shape around your region of interest. Ensure that all the roads that are
          affecting your region are included.
        </li>
        <li>
          <v-icon color="success" size="12" class="mr-1">mdi-check</v-icon>
          If you click on the download icon
          <v-icon color="primary" size="12" class="mx-1" style="vertical-align: middle"
            >mdi-download</v-icon
          >
          , the road network that falls within your indicated shape will be formatted to suit the
          requirements for SPIN and downloaded.
        </li>
        <li>
          <v-icon color="success" size="12" class="mr-1">mdi-check</v-icon>
          <span>
            Check your data after the download. You can manually alter the network if needed, for
            example by specifying any speed limits, one-way or two-way roads or by adding in new
            roads confirm your project needs.
          </span>
        </li>
      </ul>
      <a class="skip-link" @click="markComplete">I would like to skip this step</a>
    </div>
    <v-dialog
      v-model="showWarning"
      persistent
      width="480"
      transition="dialog-bottom-transition"
      :scrim="true"
    >
      <v-card
        class="d-flex flex-column align-center justify-center"
        style="padding: 32px 24px; background: #fff; border-radius: 12px"
        elevation="2"
      >
        <v-icon color="warning" size="40" class="mb-3">mdi-alert</v-icon>
        <span style="font-size: 1.1rem; font-weight: 500; text-align: center" class="mb-4">
          {{ warningMessage }}
        </span>
        <div class="d-flex justify-space-between" style="width: 100%; gap: 16px">
          <v-btn
            color="secondary"
            style="width: 100%; font-weight: 600"
            @click="showWarning = false"
          >
            Close
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
    <div style="margin-bottom: 16px">
      <div
        id="leaflet-map"
        style="width: 100%; height: 400px; border-radius: 8px; border: 1px solid #ccc"
      ></div>
    </div>
    <v-overlay :model-value="isLoading" class="map-loading-overlay" persistent>
      <div class="custom-loading-box">
        <template v-if="loadingMessage === 'Generate Data Successfully!'">
          <v-icon color="primary" size="36">mdi-check-circle-outline</v-icon>
        </template>
        <template v-else-if="loadingMessage === 'The process failed. Please try again.'">
          <v-icon color="error" size="36">mdi-information-outline</v-icon>
        </template>
        <template
          v-else-if="
            loadingMessage ===
            'This task is currently pending in a calculation queue. Please be patient.'
          "
        >
          <v-icon color="primary" size="36"> mdi-timer-sand </v-icon>
        </template>
        <template v-else>
          <v-progress-circular indeterminate color="primary" size="36"></v-progress-circular>
        </template>
        <div class="loading-message">{{ loadingMessage }}</div>
        <div class="loading-actions">
          <template v-if="isReRunVisible">
            <v-btn
              class="text-none mx-2"
              variant="tonal"
              :disabled="reRunDisabled"
              @click="handleReRun"
            >
              Re-run
            </v-btn>
          </template>
          <template v-else>
            <v-btn
              class="text-none mx-2"
              variant="tonal"
              :disabled="getFileDisabled"
              @click="
                downloadFile(
                  useGenerateInputStore().currentItem?.sas_link ?? '',
                  'generated_file.zip'
                )
              "
            >
              Get File
            </v-btn>
          </template>
          <v-btn class="text-none mx-2" variant="tonal" @click="stopPulling"> Close </v-btn>
        </div>
      </div>
    </v-overlay>
  </v-form>
</template>

<style scoped>
.data-prep-header {
  background: #f8fafc;
  border-radius: 8px;
  padding: 24px 32px 16px 32px;
  margin-bottom: 24px;
  border: 1px solid #e0e7ef;
}
.data-prep-title {
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 8px;
  color: #222;
}
.data-prep-desc {
  font-size: 1rem;
  color: #444;
  margin-bottom: 12px;
}
.data-prep-list {
  list-style: none;
  padding: 0;
  margin: 0 0 12px 0;
}
.data-prep-list li {
  align-items: flex-start;
  margin-bottom: 6px;
  font-size: 0.98rem;
  color: #333;
}
.manual-link {
  color: #009eab;
  text-decoration: underline;
}
.skip-link {
  display: inline-block;
  color: #009eab;
  text-decoration: underline;
  cursor: pointer;
  font-size: 1rem;
  margin-top: 8px;
}
#leaflet-map {
  z-index: 1;
  height: 600px !important;
}
.leaflet-control-custom {
  background: white !important;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: black !important;
}
.map-loading-overlay {
  z-index: 1000;
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.custom-loading-box {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.12);
  padding: 32px 40px 24px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 240px;
}
.loading-message {
  margin-top: 12px;
  font-size: 1.1rem;
  color: #333;
  text-align: center;
  word-break: break-word;
}
.loading-actions {
  display: flex;
  justify-content: center;
  margin-top: 16px;
}
</style>
